import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  Shield,
  LogOut,
  Search,
  Users,
  Calendar,
  CreditCard,
  AlertOctagon,
  KeyRound,
  X,
  TimerReset,
  ArrowRightCircle,
  Activity as ActivityIcon,
} from "lucide-react";
import { adminApi, clearAdminToken, getAdminToken } from "../lib/adminApi";

const TIER_FILTERS = [
  { value: "all", label: "All" },
  { value: "trial", label: "Trial" },
  { value: "expired", label: "Expired" },
  { value: "solo", label: "Solo" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function TierPill({ bucket }) {
  const cls =
    bucket === "solo"
      ? "bg-emerald-900/60 text-emerald-300 border-emerald-700"
      : bucket === "expired"
      ? "bg-red-900/50 text-red-300 border-red-800"
      : "bg-amber-900/40 text-amber-300 border-amber-800";
  return (
    <span
      className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded border ${cls}`}
    >
      {bucket}
    </span>
  );
}

function StatCard({ label, value, icon: Icon, tone = "slate", testId }) {
  const toneCls = {
    slate: "border-slate-700 bg-slate-800/60",
    amber: "border-amber-800 bg-amber-950/30",
    emerald: "border-emerald-800 bg-emerald-950/30",
    red: "border-red-800 bg-red-950/30",
  }[tone];
  return (
    <div data-testid={testId} className={`rounded-lg border p-4 ${toneCls}`}>
      <div className="flex items-center justify-between text-slate-400">
        <span className="text-[11px] uppercase tracking-[0.18em]">{label}</span>
        <Icon className="w-4 h-4 opacity-70" />
      </div>
      <div className="mt-2 text-3xl font-bold tabular-nums text-slate-100">
        {value ?? "—"}
      </div>
    </div>
  );
}

function PasswordChangeModal({ open, onClose, forced }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [next2, setNext2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) return setError("New password must be ≥8 characters.");
    if (next !== next2) return setError("New passwords do not match.");
    setBusy(true);
    try {
      await adminApi.changePassword(current, next);
      onClose(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-password-modal"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
    >
      <form
        onSubmit={submit}
        className="bg-[#1E293B] border border-slate-700 rounded-xl p-6 w-full max-w-sm shadow-2xl font-mono text-slate-100"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm uppercase tracking-[0.18em] text-emerald-400 inline-flex items-center gap-2">
            <KeyRound className="w-4 h-4" />
            {forced ? "Set a new password" : "Change password"}
          </h2>
          {!forced && (
            <button
              type="button"
              onClick={() => onClose(false)}
              className="text-slate-500 hover:text-slate-200"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {forced && (
          <p className="text-xs text-amber-300 mb-3">
            You're using the initial password. Set your own to continue.
          </p>
        )}
        <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">
          Current password
        </label>
        <input
          data-testid="admin-pw-current"
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          className="w-full bg-[#0F172A] border border-slate-700 rounded-md px-3 py-2 text-sm mb-3"
        />
        <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">
          New password
        </label>
        <input
          data-testid="admin-pw-new"
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          className="w-full bg-[#0F172A] border border-slate-700 rounded-md px-3 py-2 text-sm mb-3"
        />
        <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">
          Confirm new password
        </label>
        <input
          data-testid="admin-pw-new2"
          type="password"
          value={next2}
          onChange={(e) => setNext2(e.target.value)}
          className="w-full bg-[#0F172A] border border-slate-700 rounded-md px-3 py-2 text-sm mb-4"
        />
        {error && (
          <div className="text-xs text-red-400 mb-3 border border-red-900/60 bg-red-950/40 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <button
          data-testid="admin-pw-submit"
          disabled={busy || !current || !next || !next2}
          className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-medium rounded-md py-2.5"
        >
          {busy ? "Updating…" : "Update password"}
        </button>
      </form>
    </div>
  );
}

function UserDetailDrawer({ userId, onClose, onUserMutated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openPlanId, setOpenPlanId] = useState(null);
  const [extendDays, setExtendDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminApi.userDetail(userId);
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) load();
  }, [userId, load]);

  if (!userId) return null;

  const handleExtend = async () => {
    setBusy(true);
    setError("");
    try {
      await adminApi.extendTrial(userId, parseInt(extendDays, 10) || 0);
      await load();
      onUserMutated?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const handleTier = async (tier) => {
    if (!window.confirm(`Change tier to "${tier}"?`)) return;
    setBusy(true);
    setError("");
    try {
      await adminApi.changeTier(userId, tier);
      await load();
      onUserMutated?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    const u = data?.user;
    const email = u?.email || userId;
    const confirmed = window.confirm(
      `Permanently DELETE ${email}?\n\nThis wipes the user account, all their patients, all plans, sessions and password-reset tokens. Cannot be undone.`
    );
    if (!confirmed) return;
    // Double-confirm to avoid accidents.
    const second = window.prompt(`Type DELETE to confirm removal of ${email}:`);
    if ((second || "").trim() !== "DELETE") return;
    setBusy(true);
    setError("");
    try {
      const res = await adminApi.deleteUser(userId);
      const c = res?.counts || {};
      window.alert(
        `Deleted ${email}.\n` +
          `Users: ${c.user || 0}, Patients: ${c.patients || 0}, Plans: ${c.plans || 0}, Sessions: ${c.sessions || 0}, Resets: ${c.password_resets || 0}, Emails: ${c.pending_emails || 0}.`
      );
      onUserMutated?.();
      onClose?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const u = data?.user;
  const openPlan = data?.plans?.find((p) => p.id === openPlanId);

  return (
    <div
      data-testid="admin-user-drawer"
      className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex justify-end"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-[#0F172A] border-l border-slate-700 h-full overflow-y-auto font-mono text-slate-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-[#0F172A] border-b border-slate-700 px-5 py-3 flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.18em] text-emerald-400">
            User detail
          </div>
          <button
            data-testid="admin-drawer-close"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading && (
          <div className="p-10 text-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin inline-block" /> Loading…
          </div>
        )}
        {error && (
          <div className="m-5 text-xs text-red-400 border border-red-900/60 bg-red-950/40 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        {u && !loading && (
          <div className="p-5 space-y-5">
            <div>
              <div className="text-base font-semibold text-slate-100">
                {u.name || "(no name)"}
              </div>
              <div className="text-xs text-slate-400 break-all">{u.email}</div>
              <div className="mt-2 flex items-center gap-2 text-[11px]">
                <TierPill bucket={u.tier_bucket} />
                <span className="text-slate-500">·</span>
                <span className="text-slate-400">
                  Therapy: {u.therapy_type || "—"}
                </span>
                <span className="text-slate-500">·</span>
                <span className="text-slate-400">
                  Auth: {u.auth_provider || "password"}
                </span>
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                Signed up: {fmtDate(u.created_at)} · Trial expires:{" "}
                {fmtDate(u.trial_expires_at)}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div
                data-testid="admin-drawer-extend"
                className="rounded-lg border border-slate-700 bg-slate-800/60 p-3"
              >
                <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 inline-flex items-center gap-1.5">
                  <TimerReset className="w-3.5 h-3.5" /> Extend trial
                </div>
                <div className="flex items-center gap-2">
                  <input
                    data-testid="admin-extend-days"
                    type="number"
                    min="1"
                    max="365"
                    value={extendDays}
                    onChange={(e) => setExtendDays(e.target.value)}
                    className="w-20 bg-[#0F172A] border border-slate-700 rounded-md px-2 py-1.5 text-sm"
                  />
                  <span className="text-xs text-slate-400">days</span>
                  <button
                    data-testid="admin-extend-apply"
                    onClick={handleExtend}
                    disabled={busy}
                    className="ml-auto px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 rounded-md"
                  >
                    Apply
                  </button>
                </div>
              </div>

              <div
                data-testid="admin-drawer-tier"
                className="rounded-lg border border-slate-700 bg-slate-800/60 p-3"
              >
                <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 inline-flex items-center gap-1.5">
                  <ArrowRightCircle className="w-3.5 h-3.5" /> Set tier
                </div>
                <div className="flex gap-2">
                  {["trial", "solo", "demo"].map((t) => (
                    <button
                      key={t}
                      data-testid={`admin-set-tier-${t}`}
                      onClick={() => handleTier(t)}
                      disabled={busy}
                      className="flex-1 px-2 py-1.5 text-[11px] uppercase tracking-wider bg-slate-700 hover:bg-slate-600 rounded-md disabled:opacity-50"
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div
              data-testid="admin-drawer-delete"
              className="rounded-lg border border-red-900/70 bg-red-950/40 p-3"
            >
              <div className="text-[11px] uppercase tracking-wider text-red-300 mb-1.5">
                Danger zone
              </div>
              <div className="text-[11px] text-red-200/70 mb-2 leading-snug">
                Permanently deletes this user and every patient, plan, session,
                and pending email they own. Cannot be undone.
              </div>
              <button
                data-testid="admin-delete-user-btn"
                onClick={handleDelete}
                disabled={busy}
                className="w-full px-3 py-2 text-[11px] uppercase tracking-wider font-semibold bg-red-800 hover:bg-red-700 text-white rounded-md disabled:opacity-50"
              >
                Delete user permanently
              </button>
            </div>

            <section>
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-2">
                Patients ({data.patients.length})
              </div>
              <div className="border border-slate-700 rounded-lg divide-y divide-slate-800 max-h-64 overflow-y-auto">
                {data.patients.length === 0 && (
                  <div className="p-3 text-xs text-slate-500">No patients.</div>
                )}
                {data.patients.map((p) => (
                  <div
                    key={p.patient_id}
                    className="px-3 py-2 text-xs flex items-center justify-between"
                  >
                    <span className="text-slate-200">{p.patient_id}</span>
                    <span className="text-slate-500">{p.initials}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-2">
                Plans ({data.plans.length})
              </div>
              <div className="border border-slate-700 rounded-lg divide-y divide-slate-800 max-h-72 overflow-y-auto">
                {data.plans.length === 0 && (
                  <div className="p-3 text-xs text-slate-500">No plans.</div>
                )}
                {data.plans.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setOpenPlanId(p.id === openPlanId ? null : p.id)}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between hover:bg-slate-800/60 ${
                      openPlanId === p.id ? "bg-slate-800/60" : ""
                    }`}
                  >
                    <span className="text-slate-200">{p.patient_id}</span>
                    <span className="text-slate-500">
                      {p.date} · {p.type} · {p.status}
                    </span>
                  </button>
                ))}
              </div>

              {openPlan && (
                <pre
                  data-testid="admin-plan-content"
                  className="mt-2 max-h-72 overflow-auto bg-[#020617] border border-slate-800 text-[11px] text-slate-300 p-3 rounded-md whitespace-pre-wrap"
                >
                  {JSON.stringify(
                    {
                      id: openPlan.id,
                      type: openPlan.type,
                      status: openPlan.status,
                      date: openPlan.date,
                      therapy_type: openPlan.therapy_type,
                      patient: openPlan.patient,
                      evaluation: openPlan.evaluation,
                      daily: openPlan.daily,
                    },
                    null,
                    2
                  )}
                </pre>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function ActivityFeed({ events }) {
  if (!events?.length) return <div className="text-xs text-slate-500">No activity yet.</div>;
  const labels = {
    signup: "Signed up",
    plan_save: "Saved plan",
    trial_expired: "Trial expired",
  };
  const dots = {
    signup: "bg-emerald-400",
    plan_save: "bg-sky-400",
    trial_expired: "bg-red-400",
  };
  return (
    <ul className="space-y-1.5 max-h-[460px] overflow-y-auto pr-1">
      {events.map((e, i) => (
        <li
          key={`${e.type}-${e.user_id}-${e.at}-${i}`}
          data-testid={`admin-activity-${e.type}`}
          className="flex items-start gap-2 text-xs leading-snug"
        >
          <span className={`mt-1.5 w-1.5 h-1.5 rounded-full ${dots[e.type] || "bg-slate-400"}`} />
          <div className="flex-1 min-w-0">
            <div className="text-slate-300">
              <span className="text-slate-200 font-medium">{labels[e.type] || e.type}</span>
              <span className="text-slate-500"> · </span>
              <span className="text-slate-400">{e.email || e.user_id}</span>
            </div>
            <div className="text-[10px] text-slate-500">{fmtDate(e.at)}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [openUserId, setOpenUserId] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForced, setPwForced] = useState(false);

  // initial auth check
  useEffect(() => {
    if (!getAdminToken()) {
      navigate("/admin/login");
      return;
    }
    adminApi
      .me()
      .then((m) => {
        setMe(m);
        setAuthChecked(true);
        if (m.must_change_password) {
          setPwForced(true);
          setPwOpen(true);
        }
      })
      .catch(() => {
        clearAdminToken();
        navigate("/admin/login");
      });
  }, [navigate]);

  const reloadAll = useCallback(async () => {
    const [s, u, a] = await Promise.all([
      adminApi.stats(),
      adminApi.users({ tier: filter, q }),
      adminApi.activity(40),
    ]);
    setStats(s);
    setUsers(u.users);
    setActivity(a.events);
  }, [filter, q]);

  useEffect(() => {
    if (authChecked) reloadAll();
  }, [authChecked, reloadAll]);

  const logout = () => {
    clearAdminToken();
    navigate("/admin/login");
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-[#0F172A] text-slate-400 flex items-center justify-center font-mono">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  return (
    <div
      data-testid="admin-page"
      className="min-h-screen bg-[#0F172A] text-slate-100 font-mono"
    >
      <header className="border-b border-slate-700 bg-[#0B1220]">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 text-emerald-400">
            <Shield className="w-4 h-4" />
            <span className="text-xs uppercase tracking-[0.22em] font-semibold">
              TherapyNote · Admin
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <span>{me?.username}</span>
            <button
              data-testid="admin-change-password-btn"
              onClick={() => {
                setPwForced(false);
                setPwOpen(true);
              }}
              className="inline-flex items-center gap-1 hover:text-slate-100"
            >
              <KeyRound className="w-3.5 h-3.5" /> Change password
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-1 hover:text-slate-100"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Stats */}
        <section
          data-testid="admin-stats"
          className="grid grid-cols-2 md:grid-cols-4 gap-3"
        >
          <StatCard
            label="Total users"
            value={stats?.total}
            icon={Users}
            tone="slate"
            testId="admin-stat-total"
          />
          <StatCard
            label="On trial"
            value={stats?.trial}
            icon={Calendar}
            tone="amber"
            testId="admin-stat-trial"
          />
          <StatCard
            label="Solo (paid)"
            value={stats?.solo}
            icon={CreditCard}
            tone="emerald"
            testId="admin-stat-solo"
          />
          <StatCard
            label="Expired"
            value={stats?.expired}
            icon={AlertOctagon}
            tone="red"
            testId="admin-stat-expired"
          />
        </section>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Users table */}
          <section className="lg:col-span-2 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs uppercase tracking-[0.18em] text-slate-400">
                Users
              </div>
              <div className="flex items-center gap-2">
                {TIER_FILTERS.map((t) => (
                  <button
                    key={t.value}
                    data-testid={`admin-filter-${t.value}`}
                    onClick={() => setFilter(t.value)}
                    className={`px-2.5 py-1 text-[11px] uppercase tracking-wider rounded-md border ${
                      filter === t.value
                        ? "bg-emerald-600 border-emerald-500 text-white"
                        : "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    data-testid="admin-search"
                    placeholder="Search email or name"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="pl-7 pr-3 py-1.5 text-xs bg-slate-800 border border-slate-700 rounded-md w-56 focus:outline-none focus:border-emerald-500"
                  />
                </div>
              </div>
            </div>

            <div
              data-testid="admin-users-table"
              className="border border-slate-700 rounded-lg overflow-hidden"
            >
              <div className="grid grid-cols-12 px-4 py-2 bg-slate-800/60 text-[10px] uppercase tracking-[0.16em] text-slate-400 border-b border-slate-700">
                <div className="col-span-4">Email · Name</div>
                <div className="col-span-2">Therapy</div>
                <div className="col-span-2">Tier</div>
                <div className="col-span-1 text-right">Trial</div>
                <div className="col-span-1 text-right">Pat.</div>
                <div className="col-span-1 text-right">Plans</div>
                <div className="col-span-1 text-right">Signup</div>
              </div>
              <div className="max-h-[560px] overflow-y-auto divide-y divide-slate-800">
                {users.length === 0 && (
                  <div className="px-4 py-8 text-xs text-slate-500 text-center">
                    No users match this filter.
                  </div>
                )}
                {users.map((u) => (
                  <button
                    key={u.user_id}
                    data-testid={`admin-user-row-${u.user_id}`}
                    onClick={() => setOpenUserId(u.user_id)}
                    className="w-full text-left grid grid-cols-12 px-4 py-2.5 text-xs hover:bg-slate-800/60 items-center"
                  >
                    <div className="col-span-4 min-w-0">
                      <div className="truncate text-slate-100">{u.email}</div>
                      <div className="truncate text-slate-500 text-[11px]">
                        {u.name || "—"}
                      </div>
                    </div>
                    <div className="col-span-2 text-slate-300 capitalize">
                      {u.therapy_type}
                    </div>
                    <div className="col-span-2">
                      <TierPill bucket={u.tier_bucket} />
                    </div>
                    <div className="col-span-1 text-right text-slate-300 tabular-nums">
                      {u.tier_bucket === "trial"
                        ? `${u.trial_days_left}d`
                        : "—"}
                    </div>
                    <div className="col-span-1 text-right text-slate-300 tabular-nums">
                      {u.patient_count}
                    </div>
                    <div className="col-span-1 text-right text-slate-300 tabular-nums">
                      {u.plan_count}
                    </div>
                    <div className="col-span-1 text-right text-slate-500 text-[11px]">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Activity feed */}
          <section data-testid="admin-activity-feed" className="space-y-3">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-400 inline-flex items-center gap-1.5">
              <ActivityIcon className="w-3.5 h-3.5" /> Activity
            </div>
            <div className="border border-slate-700 rounded-lg p-3 bg-slate-900/50">
              <ActivityFeed events={activity} />
            </div>
          </section>
        </div>
      </main>

      <UserDetailDrawer
        userId={openUserId}
        onClose={() => setOpenUserId(null)}
        onUserMutated={reloadAll}
      />
      <PasswordChangeModal
        open={pwOpen}
        forced={pwForced}
        onClose={(success) => {
          if (success || !pwForced) setPwOpen(false);
          if (success) setPwForced(false);
        }}
      />
    </div>
  );
}
